import { useEffect, useState } from 'react';
import { BOOKING_TYPES, type BookingType } from '@makemypartner/shared';
import { supabase } from '@/lib/supabase';

/**
 * The service catalogue, per docs/project.md §2 and §10.
 *
 * The model that matters here: a SERVICE is what you do together, and a
 * BOOKING TYPE is how you meet. They are independent. Conversation Partner
 * works over chat, voice, video or across a table; Dining Partner only works
 * in person. Each category carries the set of channels it allows, and a
 * companion sets a rate per channel.
 *
 * Read straight from the database — RLS limits these to active rows, so no API
 * endpoint is needed.
 */

/**
 * Re-exported so the pages that already import these from here keep working.
 * The definition itself lives in @makemypartner/shared beside the other
 * Postgres enum mirrors — it used to be declared twice, here and in the API,
 * which is exactly the drift that file exists to prevent.
 */
export { BOOKING_TYPES };
export type { BookingType };

export interface ServiceCategory {
  id: string;
  slug: string;
  name: string;
  description: string;
  guidance: string;
  allowedBookingTypes: BookingType[];
  /** Which drawing to show. See ICON_KEYS in components/companion/ServiceIcon. */
  iconKey: string;
  /** Accent hue, 0-359. Rendered through categoryTint(). */
  hue: number;
}

export interface City {
  id: string;
  slug: string;
  name: string;
  state: string;
}

export interface Rate {
  bookingType: BookingType;
  priceCoins: number;
  /** 'per_minute' for the remote channels, 'per_hour' in person. */
  unit: 'per_minute' | 'per_hour';
}

export interface ServiceListing {
  id: string;
  companionId: string;
  categorySlug: string;
  categoryName: string;
  allowedBookingTypes: BookingType[];
  headline: string;
  description: string;
  experienceNote: string;
  displayName: string;
  avatarUrl: string | null;
  citySlug: string | null;
  cityName: string | null;
  rating: number;
  ratingCount: number;
  rates: Rate[];
}

export const bookingTypeLabel = (t: BookingType): string =>
  ({ chat: 'Chat', voice: 'Voice call', video: 'Video call', in_person: 'In person' })[t];

export const unitLabel = (u: Rate['unit']): string =>
  u === 'per_hour' ? '/hour' : '/min';

/** In-person is the only channel that needs a place — and therefore a city. */
export const isRemote = (t: BookingType): boolean => t !== 'in_person';

/** The cheapest way to book this listing, for the "from X coins" line. */
export function cheapestRate(rates: Rate[]): Rate | null {
  if (rates.length === 0) return null;
  return rates.reduce((a, b) => (b.priceCoins < a.priceCoins ? b : a));
}

/* ── Categories ───────────────────────────────────────────────────────────── */

interface CategoryRow {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  guidance: string | null;
  allowed_booking_types: BookingType[];
  icon_key: string | null;
  hue: number | null;
}

const toCategory = (r: CategoryRow): ServiceCategory => ({
  id: r.id,
  slug: r.slug,
  name: r.name,
  description: r.description ?? '',
  guidance: r.guidance ?? '',
  allowedBookingTypes: r.allowed_booking_types ?? [],
  iconKey: r.icon_key ?? 'circle',
  hue: r.hue ?? 210,
});

export function useServiceCategories() {
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    void supabase
      .from('service_categories')
      .select('id, slug, name, description, guidance, allowed_booking_types, icon_key, hue')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data, error: err }) => {
        if (!active) return;
        if (err) setError(err.message);
        else setCategories((data as unknown as CategoryRow[]).map(toCategory));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  return { categories, loading, error };
}

/* ── Price bands ──────────────────────────────────────────────────────────── */

export interface Band {
  bookingType: BookingType;
  min: number;
  max: number;
}

export function useBands() {
  const [bands, setBands] = useState<Record<string, Band>>({});

  useEffect(() => {
    let active = true;

    void supabase
      .from('booking_type_bands')
      .select('booking_type, min_price_coins, max_price_coins')
      .then(({ data }) => {
        if (!active || !data) return;
        const map: Record<string, Band> = {};
        for (const r of data as unknown as {
          booking_type: BookingType;
          min_price_coins: number;
          max_price_coins: number;
        }[]) {
          map[r.booking_type] = {
            bookingType: r.booking_type,
            min: r.min_price_coins,
            max: r.max_price_coins,
          };
        }
        setBands(map);
      });

    return () => {
      active = false;
    };
  }, []);

  return bands;
}

/* ── Cities ───────────────────────────────────────────────────────────────── */

export function useCities() {
  const [cities, setCities] = useState<City[]>([]);

  useEffect(() => {
    let active = true;

    void supabase
      .from('cities')
      .select('id, slug, name, state')
      .eq('is_active', true)
      .order('sort_order')
      .then(({ data }) => {
        if (active && data) setCities(data as unknown as City[]);
      });

    return () => {
      active = false;
    };
  }, []);

  return cities;
}

/**
 * The city someone last chose, kept in localStorage.
 *
 * Deliberately not asked for at signup. Most people arrive wanting to look
 * around, and a location prompt before anything has been shown is the fastest
 * way to lose them.
 */
const CITY_KEY = 'mmp.city';

export function useSelectedCity(): [string | null, (slug: string | null) => void] {
  const [city, setCity] = useState<string | null>(() => localStorage.getItem(CITY_KEY));

  const update = (slug: string | null) => {
    setCity(slug);
    if (slug) localStorage.setItem(CITY_KEY, slug);
    else localStorage.removeItem(CITY_KEY);
  };

  return [city, update];
}

/* ── Browse ───────────────────────────────────────────────────────────────── */

interface ListingRow {
  id: string;
  companion_id: string;
  category_slug: string;
  category_name: string;
  allowed_booking_types: BookingType[];
  headline: string | null;
  description: string | null;
  experience_note: string | null;
  display_name: string;
  avatar_url: string | null;
  city_slug: string | null;
  city_name: string | null;
  rating_avg: number;
  rating_count: number;
  rates: { booking_type: BookingType; price_coins: number; unit: Rate['unit'] }[];
}

const toListing = (r: ListingRow): ServiceListing => ({
  id: r.id,
  companionId: r.companion_id,
  categorySlug: r.category_slug,
  categoryName: r.category_name,
  allowedBookingTypes: r.allowed_booking_types ?? [],
  headline: r.headline ?? '',
  description: r.description ?? '',
  experienceNote: r.experience_note ?? '',
  displayName: r.display_name || 'Companion',
  avatarUrl: r.avatar_url,
  citySlug: r.city_slug,
  cityName: r.city_name,
  rating: Number(r.rating_avg) || 0,
  ratingCount: r.rating_count,
  rates: (r.rates ?? []).map((x) => ({
    bookingType: x.booking_type,
    priceCoins: x.price_coins,
    unit: x.unit,
  })),
});

export interface BrowseFilters {
  city: string | null;
  bookingType: BookingType | null;
  maxPrice: number | null;
}

/**
 * Companions offering one service.
 *
 * The city filter applies only to in-person bookings. A Virtual Companion in
 * Kochi serves someone in Delhi perfectly well, so filtering remote listings
 * by city would hide most of the supply for no reason.
 */
export function useServiceListings(categorySlug: string | undefined, filters: BrowseFilters) {
  const [listings, setListings] = useState<ServiceListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { city, bookingType, maxPrice } = filters;

  useEffect(() => {
    if (!categorySlug) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);

    void supabase
      .from('v_browse_services')
      .select('*')
      .eq('category_slug', categorySlug)
      .order('rating_avg', { ascending: false })
      .then(({ data, error: err }) => {
        if (!active) return;
        if (err) {
          setError(err.message);
          setListings([]);
          setLoading(false);
          return;
        }

        let rows = (data as unknown as ListingRow[]).map(toListing);

        // Filtered here rather than in SQL because rates arrive as aggregated
        // JSON — PostgREST cannot filter inside that. The result set is one
        // category's listings, so this is small. It moves into an RPC when
        // the catalogue is big enough for that to stop being true.
        if (bookingType) {
          rows = rows.filter((l) => l.rates.some((r) => r.bookingType === bookingType));
        }

        if (city) {
          rows = rows.filter((l) => {
            const remoteOnly = l.rates.every((r) => isRemote(r.bookingType));
            return remoteOnly || l.citySlug === city;
          });
        }

        if (maxPrice !== null) {
          rows = rows.filter((l) => {
            const relevant = bookingType
              ? l.rates.filter((r) => r.bookingType === bookingType)
              : l.rates;
            return relevant.some((r) => r.priceCoins <= maxPrice);
          });
        }

        setError(null);
        setListings(rows);
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [categorySlug, city, bookingType, maxPrice]);

  return { listings, loading, error };
}

/** Every service one companion offers, for their public profile. */
export function useCompanionServices(companionId: string | undefined) {
  const [services, setServices] = useState<ServiceListing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companionId) {
      setLoading(false);
      return;
    }
    let active = true;

    void supabase
      .from('v_browse_services')
      .select('*')
      .eq('companion_id', companionId)
      .then(({ data }) => {
        if (!active) return;
        if (data) setServices((data as unknown as ListingRow[]).map(toListing));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [companionId]);

  return { services, loading };
}
